import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import SignalRService from '../../utilities/WS/SignalRService';
import {
    GetPublicConfigBL,
    GetRankingBL,
    determineScreamCategory,
    formatDisplayName
} from '../../utilities/BL/PersonalNoisemeter';

/**
 * Normaliza un ítem de participante del ranking tolerando camelCase y PascalCase.
 * Garantiza el cumplimiento estricto de CERO PII: no propaga correos ni teléfonos.
 * 
 * @param {Object} raw Objeto crudo del backend o evento SignalR.
 * @param {number} fallbackIndex Índice de posición en caso de no venir especificado.
 * @returns {Object} Participante normalizado con { id, position, displayName, decibelsPeak, category }
 */
const normalizeRankingItem = (raw, fallbackIndex = 0) => {
    if (!raw) return null;

    const id = raw.participantId || raw.ParticipantId || raw.id || raw.Id || `item-${fallbackIndex}`;
    const position = raw.rankingPosition !== undefined ? raw.rankingPosition
        : raw.RankingPosition !== undefined ? raw.RankingPosition
        : raw.position !== undefined ? raw.position
        : raw.Position !== undefined ? raw.Position
        : fallbackIndex + 1;

    const rawName = raw.displayName || raw.DisplayName || raw.fullName || raw.FullName || raw.alias || raw.Alias || `Participante #${position}`;
    const displayName = formatDisplayName(rawName, true);

    const peakValue = raw.decibelsPeak !== undefined ? raw.decibelsPeak
        : raw.DecibelsPeak !== undefined ? raw.DecibelsPeak
        : raw.peakDecibels !== undefined ? raw.peakDecibels
        : raw.PeakDecibels !== undefined ? raw.PeakDecibels
        : raw.score !== undefined ? raw.score
        : raw.Score !== undefined ? raw.Score
        : 0;
    const decibelsPeak = Math.round(Number(peakValue) * 10) / 10;

    const category = raw.category || raw.Category || determineScreamCategory(decibelsPeak);

    return {
        id: String(id),
        position: Number(position),
        displayName,
        decibelsPeak,
        category
    };
};

/**
 * Obtiene la configuración visual y estilo temático patrio según la categoría del grito.
 * 
 * @param {string} category Categoría calculada del grito.
 * @param {number} peak Decibeles pico.
 * @returns {{ title: string, badgeBg: string, textColor: string, border: string, icon: string, glow: string }}
 */
const getCategoryStyle = (category = '', peak = 0) => {
    const p = Number(peak) || 0;
    if (p >= 110.0 || category.includes('Legendario')) {
        return {
            title: '¡Grito Legendario!',
            badgeBg: 'linear-gradient(135deg, #FFD700 0%, #FFA000 100%)',
            textColor: '#0a0e14',
            border: '1px solid #FFE082',
            icon: 'bi-trophy-fill',
            glow: 'rgba(255, 215, 0, 0.55)'
        };
    }
    if (p >= 95.0 || category.includes('Patriota')) {
        return {
            title: '¡Grito Patriota!',
            badgeBg: 'linear-gradient(135deg, #CE1126 0%, #8B0000 100%)',
            textColor: '#ffffff',
            border: '1px solid #EF5350',
            icon: 'bi-fire',
            glow: 'rgba(206, 17, 38, 0.55)'
        };
    }
    if (p >= 80.0 || category.includes('Bravo')) {
        return {
            title: '¡Grito Bravo!',
            badgeBg: 'linear-gradient(135deg, #006847 0%, #004D40 100%)',
            textColor: '#ffffff',
            border: '1px solid #4CAF50',
            icon: 'bi-megaphone-fill',
            glow: 'rgba(0, 104, 71, 0.55)'
        };
    }
    return {
        title: '¡Grito con Orgullo!',
        badgeBg: 'linear-gradient(135deg, #0288D1 0%, #01579B 100%)',
        textColor: '#ffffff',
        border: '1px solid #4FC3F7',
        icon: 'bi-heart-fill',
        glow: 'rgba(2, 136, 209, 0.55)'
    };
};

/**
 * COMPONENTE OVERLAY: Pantalla Pública de Ranking en Vivo - Grito Mexicano (HU-06)
 * 
 * Características principales:
 * 1. Conexión y sincronización en tiempo real vía SignalR con aislamiento por `roomId`.
 * 2. Visualización estricta sin PII (únicamente Posición, Alias moderado, Decibel Pico y Categoría).
 * 3. Diseño de alto impacto para pantallas gigantes LED (1080p, 4K), proyectores y móviles.
 * 4. Podio escénico Top 3 (Oro, Plata, Bronce) + Grid Top 4 al 20 con animación de pulso esmeralda.
 * 5. Cero controles administrativos (vista de transmisión 100% limpia).
 * 
 * @param {{ roomId?: string, config?: Object }} props Propiedades opcionales del overlay.
 * @returns {JSX.Element}
 */
export const PersonalNoisemeterRanking = ({ roomId: propRoomId, config: propConfig }) => {
    // 1. Extracción e inferencia de Sala
    const [searchParams] = useSearchParams();
    const activeRoomId = useMemo(() => {
        return propRoomId || searchParams.get('roomId') || localStorage.getItem('active_room_id') || '';
    }, [propRoomId, searchParams]);

    // 2. Estados de Configuración y Telemetría
    const [config, setConfig] = useState(() => ({
        headerTitle: '¡GRITO MEXICANO!',
        headerSubtitle: 'TABLA OFICIAL DE POSICIONES EN VIVO',
        primaryColor: '#006847',
        backgroundColor: '#0a0e14',
        isActive: true,
        ...propConfig
    }));

    const [rankingList, setRankingList] = useState([]);
    const [stats, setStats] = useState({
        totalParticipants: 0,
        recordPeakDecibels: 0,
        averageScore: 0
    });
    const [isLoading, setIsLoading] = useState(true);

    // Registro de IDs con actualizaciones recientes para animación de pulso
    const [recentlyUpdatedIds, setRecentlyUpdatedIds] = useState(new Set());
    const pulseTimerRef = useRef(null);

    // Generar URL pública para que los asistentes escaneen el QR si desean participar
    const joinUrl = useMemo(() => {
        const origin = window.location.origin;
        const query = activeRoomId ? `?roomId=${encodeURIComponent(activeRoomId)}` : '';
        return `${origin}/grito-mexicano${query}`;
    }, [activeRoomId]);

    // =========================================================================
    // 3. CARGA INICIAL DE DATOS VÍA HTTP REST (BL)
    // =========================================================================
    const loadRankingData = useCallback(async () => {
        try {
            // Cargar configuración pública de la sala
            const configRes = await GetPublicConfigBL(activeRoomId);
            if (configRes.success && configRes.data) {
                setConfig(prev => ({
                    ...prev,
                    headerTitle: configRes.data.headerTitle || prev.headerTitle,
                    headerSubtitle: configRes.data.headerSubtitle || prev.headerSubtitle,
                    primaryColor: configRes.data.primaryColor || prev.primaryColor,
                    backgroundColor: configRes.data.backgroundColor || prev.backgroundColor,
                    isActive: configRes.data.isActive !== undefined ? configRes.data.isActive : prev.isActive
                }));
            }

            // Cargar top 20 oficial
            const rankingRes = await GetRankingBL(activeRoomId, 20);
            if (rankingRes.success && rankingRes.data) {
                const rawList = rankingRes.data.topRanking || rankingRes.data.TopRanking || [];
                const normalized = rawList.map((item, index) => normalizeRankingItem(item, index)).filter(Boolean);
                setRankingList(normalized);

                const total = rankingRes.data.totalParticipants ?? rankingRes.data.TotalParticipants ?? normalized.length;
                const record = rankingRes.data.recordPeakDecibels ?? rankingRes.data.RecordPeakDecibels ?? (normalized.length > 0 ? normalized[0].decibelsPeak : 0);
                const avg = rankingRes.data.averageScore ?? rankingRes.data.AverageScore ?? 0;

                setStats({
                    totalParticipants: Number(total) || 0,
                    recordPeakDecibels: Math.round(Number(record) * 10) / 10,
                    averageScore: Math.round(Number(avg) * 10) / 10
                });
            }
        } catch (err) {
            console.warn('[PersonalNoisemeterRanking] Error en carga inicial:', err);
        } finally {
            setIsLoading(false);
        }
    }, [activeRoomId]);

    useEffect(() => {
        loadRankingData();
    }, [loadRankingData]);

    // =========================================================================
    // 4. SUSCRIPCIÓN EN TIEMPO REAL CON SIGNALR (WEBSOCKET)
    // =========================================================================
    useEffect(() => {
        /**
         * Manejador centralizado de mensajes entrantes del Hub de SignalR.
         * Aplica aislamiento estricto por activeRoomId y actualiza el ranking en vivo.
         */
        const handleSignalRMessage = (message) => {
            if (!message || !message.type) return;

            // Aislamiento de sala: ignorar mensajes destinados a otras salas
            const msgRoomId = message.roomId || message.RoomId;
            if (activeRoomId && msgRoomId && String(msgRoomId).toLowerCase() !== String(activeRoomId).toLowerCase()) {
                return;
            }

            const type = message.type;
            const data = message.data || message.payload || {};

            // Caso 1: Actualización de posiciones o nuevos gritos registrados
            if (type === 'personalNoisemeter:rankingUpdate') {
                const rawList = data.topRanking || data.TopRanking || [];
                const normalized = rawList.map((item, index) => normalizeRankingItem(item, index)).filter(Boolean);

                // Detectar participantes actualizados para encender el pulso esmeralda
                const updatedIds = new Set();
                normalized.forEach(item => {
                    const existing = rankingList.find(r => r.id === item.id);
                    if (!existing || existing.decibelsPeak !== item.decibelsPeak || existing.position !== item.position) {
                        updatedIds.add(item.id);
                    }
                });

                if (updatedIds.size > 0) {
                    setRecentlyUpdatedIds(updatedIds);
                    if (pulseTimerRef.current) clearTimeout(pulseTimerRef.current);
                    pulseTimerRef.current = setTimeout(() => {
                        setRecentlyUpdatedIds(new Set());
                    }, 4000);
                }

                setRankingList(normalized);

                const total = data.totalParticipants ?? data.TotalParticipants ?? normalized.length;
                const record = data.recordPeakDecibels ?? data.RecordPeakDecibels ?? (normalized.length > 0 ? normalized[0].decibelsPeak : 0);
                const avg = data.averageScore ?? data.AverageScore ?? 0;

                setStats({
                    totalParticipants: Number(total) || 0,
                    recordPeakDecibels: Math.round(Number(record) * 10) / 10,
                    averageScore: Math.round(Number(avg) * 10) / 10
                });
            }

            // Caso 2: Operador actualizó configuración de textos o colores en vivo
            if (type === 'personalNoisemeter:configUpdate') {
                setConfig(prev => ({
                    ...prev,
                    headerTitle: data.headerTitle || prev.headerTitle,
                    headerSubtitle: data.headerSubtitle || prev.headerSubtitle,
                    primaryColor: data.primaryColor || prev.primaryColor,
                    backgroundColor: data.backgroundColor || prev.backgroundColor,
                    isActive: data.isActive !== undefined ? data.isActive : prev.isActive
                }));
            }
        };

        // Suscribirse a SignalR
        SignalRService.on('message', handleSignalRMessage);

        return () => {
            // Limpieza al desmontar
            SignalRService.off('message', handleSignalRMessage);
            if (pulseTimerRef.current) clearTimeout(pulseTimerRef.current);
        };
    }, [activeRoomId, rankingList]);

    // =========================================================================
    // 5. DIVISIONES DEL TOP RANKING (PODIO TOP 3 vs TABLA TOP 4-20)
    // =========================================================================
    const firstPlace = useMemo(() => rankingList[0] || null, [rankingList]);
    const secondPlace = useMemo(() => rankingList[1] || null, [rankingList]);
    const thirdPlace = useMemo(() => rankingList[2] || null, [rankingList]);
    const remainingList = useMemo(() => rankingList.slice(3), [rankingList]);

    return (
        <div
            className="personal-noisemeter-ranking-broadcast position-relative min-vh-100 w-100 d-flex flex-column justify-content-between overflow-x-hidden"
            style={{
                backgroundColor: config.backgroundColor || '#0a0e14',
                color: '#ffffff',
                fontFamily: '"Montserrat", "Segoe UI", system-ui, -apple-system, sans-serif'
            }}
        >
            {/* FONDO PATRIO MULTIDESTELLO */}
            <div className="bg-radial-patriota" />

            {/* ========================================================================= */}
            {/* 1. HEADER DE BROADCAST ESTADIAL */}
            {/* ========================================================================= */}
            <header className="position-relative z-1 px-4 py-3 px-lg-5 border-bottom border-white border-opacity-10 d-flex flex-wrap align-items-center justify-content-between gap-3 bg-black bg-opacity-40">
                {/* Lado Izquierdo: Insignia En Vivo y Título Dinámico */}
                <div className="d-flex align-items-center gap-3">
                    <div
                        className="rounded-circle d-flex align-items-center justify-content-center shadow"
                        style={{
                            width: '52px',
                            height: '52px',
                            background: 'linear-gradient(135deg, #006847 0%, #004D40 100%)',
                            border: '2px solid #FFD700',
                            boxShadow: '0 0 18px rgba(0, 104, 71, 0.6)'
                        }}
                    >
                        <i className="bi bi-megaphone-fill text-warning fs-4" />
                    </div>

                    <div>
                        <div className="d-flex align-items-center gap-2">
                            <span className="badge rounded-pill bg-danger px-3 py-1 text-white fw-bold d-inline-flex align-items-center gap-1 live-badge-pulse" style={{ fontSize: '11px', letterSpacing: '1px' }}>
                                <span className="spinner-grow spinner-grow-sm text-white" style={{ width: '8px', height: '8px' }} role="status" />
                                🔴 EN VIVO
                            </span>
                            <span className="text-secondary small fw-semibold text-uppercase tracking-wider" style={{ letterSpacing: '1px', fontSize: '12px' }}>
                                FIESTAS PATRIAS • EDICIÓN ESPECIAL
                            </span>
                        </div>
                        <h2 className="mb-0 fw-black text-white text-uppercase tracking-tight" style={{ fontWeight: 900, letterSpacing: '-0.5px' }}>
                            {config.headerTitle || '¡GRITO MEXICANO!'}
                        </h2>
                        <p className="mb-0 text-warning text-opacity-75 small fw-semibold">
                            {config.headerSubtitle || 'TABLA OFICIAL DE POSICIONES EN VIVO'}
                        </p>
                    </div>
                </div>

                {/* Lado Derecho: Telemetría Global de Sala (Récord + Total de Gritos) */}
                <div className="d-flex align-items-center gap-3 ms-auto">
                    {/* Tarjeta de Récord del Evento */}
                    <div className="glass-card-broadcast px-3 py-2 rounded-4 text-center border-warning border-opacity-50">
                        <div className="text-secondary text-uppercase fw-bold" style={{ fontSize: '10px', letterSpacing: '1px' }}>
                            🏆 RÉCORD DEL EVENTO
                        </div>
                        <div className="fs-4 fw-black gold-text-shimmer lh-1 mt-1" style={{ fontWeight: 900 }}>
                            {stats.recordPeakDecibels > 0 ? `${stats.recordPeakDecibels} dB` : '--.- dB'}
                        </div>
                    </div>

                    {/* Tarjeta de Total Gritos */}
                    <div className="glass-card-broadcast px-3 py-2 rounded-4 text-center border-success border-opacity-50">
                        <div className="text-secondary text-uppercase fw-bold" style={{ fontSize: '10px', letterSpacing: '1px' }}>
                            🇲🇽 GRITOS REGISTRADOS
                        </div>
                        <div className="fs-4 fw-black text-success lh-1 mt-1" style={{ fontWeight: 900 }}>
                            {stats.totalParticipants}
                        </div>
                    </div>

                    {/* QR Compacto para Unirse */}
                    <div className="d-none d-md-flex align-items-center gap-2 glass-card-broadcast p-2 rounded-3 border-secondary border-opacity-25">
                        <QRCodeSVG
                            value={joinUrl}
                            size={44}
                            bgColor="transparent"
                            fgColor="#ffffff"
                            level="M"
                        />
                        <div className="text-start" style={{ lineHeight: '1.1' }}>
                            <div className="text-warning fw-bold" style={{ fontSize: '10px' }}>¡GRITA AHORA!</div>
                            <div className="text-secondary" style={{ fontSize: '9px' }}>Escanea y participa</div>
                        </div>
                    </div>
                </div>
            </header>

            {/* ========================================================================= */}
            {/* 2. CUERPO PRINCIPAL: PODIO TOP 3 + TABLA TOP 4-20 */}
            {/* ========================================================================= */}
            <main className="position-relative z-1 flex-grow-1 px-3 py-4 px-lg-5 d-flex flex-column justify-content-center">
                {isLoading ? (
                    <div className="d-flex flex-column align-items-center justify-content-center py-5 text-center">
                        <div className="spinner-border text-warning mb-3" style={{ width: '3.5rem', height: '3.5rem' }} role="status" />
                        <h4 className="fw-bold text-white">Sincronizando Ranking Oficial...</h4>
                        <p className="text-secondary small">Conectando con la sala en tiempo real</p>
                    </div>
                ) : rankingList.length === 0 ? (
                    /* ESTADO VACÍO FESTIVO */
                    <div className="d-flex flex-column align-items-center justify-content-center py-5 text-center my-auto">
                        <div
                            className="glass-card-broadcast p-5 rounded-5 border-warning border-opacity-50 shadow-lg text-center position-relative overflow-hidden"
                            style={{ maxWidth: '600px' }}
                        >
                            <div
                                className="position-absolute top-0 start-0 w-100"
                                style={{
                                    height: '6px',
                                    background: 'linear-gradient(90deg, #006847 0%, #006847 33%, #FFFFFF 33%, #FFFFFF 66%, #CE1126 66%, #CE1126 100%)'
                                }}
                            />

                            <div className="d-inline-flex p-4 rounded-circle bg-success bg-opacity-20 border border-success border-opacity-50 mb-4 trophy-badge-glow">
                                <i className="bi bi-megaphone-fill text-warning fs-1" />
                            </div>

                            <h2 className="fw-black text-white text-uppercase mb-2" style={{ fontWeight: 900 }}>
                                ¡Sé el primero en dar el Grito Patrio!
                            </h2>
                            <p className="text-light text-opacity-75 mb-4 fs-6">
                                Acércate, abre la cámara de tu teléfono y demuestra la máxima potencia de tu voz para liderar el ranking del estadio.
                            </p>

                            <div className="d-flex justify-content-center mb-3">
                                <div className="p-3 bg-white rounded-4 shadow-lg">
                                    <QRCodeSVG
                                        value={joinUrl}
                                        size={180}
                                        bgColor="#ffffff"
                                        fgColor="#0a0e14"
                                        level="H"
                                    />
                                </div>
                            </div>

                            <div className="badge bg-dark border border-secondary text-warning px-3 py-2 rounded-pill small">
                                <i className="bi bi-qr-code me-1" /> Escanea el código QR para comenzar
                            </div>
                        </div>
                    </div>
                ) : (
                    /* CONTENIDO DE RANKING ACTIVO */
                    <div className="w-100 py-2">
                        {/* ------------------------------------------------------------- */}
                        {/* A. PODIO TOP 3 (ORO, PLATA, BRONCE) */}
                        {/* ------------------------------------------------------------- */}
                        <div className="row g-3 g-lg-4 align-items-end justify-content-center mb-4">
                            {/* 2DO LUGAR (PLATA) */}
                            <div className="col-12 col-md-4 order-2 order-md-1">
                                {secondPlace ? (
                                    <div
                                        className={`glass-card-broadcast podium-second-card p-4 rounded-4 text-center position-relative ${recentlyUpdatedIds.has(secondPlace.id) ? 'emerald-pulse-active' : ''}`}
                                    >
                                        <div
                                            className="position-absolute top-0 start-50 translate-middle badge rounded-pill px-3 py-1 shadow"
                                            style={{ backgroundColor: '#E0E6ED', color: '#0a0e14', fontWeight: 900, fontSize: '13px', border: '2px solid #ffffff' }}
                                        >
                                            🥈 2º LUGAR
                                        </div>

                                        <div className="mt-2 mb-1">
                                            <i className="bi bi-award-fill text-light fs-2 opacity-75" />
                                        </div>

                                        <h4 className="fw-bold text-white mb-1 text-truncate" title={secondPlace.displayName}>
                                            {secondPlace.displayName}
                                        </h4>

                                        <div className="d-inline-flex align-items-baseline gap-1 my-2">
                                            <span className="display-6 fw-black text-white" style={{ fontWeight: 900 }}>
                                                {secondPlace.decibelsPeak}
                                            </span>
                                            <span className="text-secondary fw-bold fs-5">dB</span>
                                        </div>

                                        {(() => {
                                            const catStyle = getCategoryStyle(secondPlace.category, secondPlace.decibelsPeak);
                                            return (
                                                <div>
                                                    <span
                                                        className="badge rounded-pill px-3 py-1 fw-bold small text-truncate"
                                                        style={{
                                                            background: catStyle.badgeBg,
                                                            color: catStyle.textColor,
                                                            border: catStyle.border,
                                                            boxShadow: `0 0 10px ${catStyle.glow}`,
                                                            maxWidth: '100%'
                                                        }}
                                                    >
                                                        <i className={`bi ${catStyle.icon} me-1`} />
                                                        {catStyle.title}
                                                    </span>
                                                </div>
                                            );
                                        })()}
                                    </div>
                                ) : (
                                    <div className="glass-card-broadcast p-4 rounded-4 text-center border-secondary border-opacity-25 opacity-50">
                                        <span className="badge bg-secondary mb-2">🥈 2º LUGAR</span>
                                        <p className="text-secondary small mb-0">Posición disponible</p>
                                    </div>
                                )}
                            </div>

                            {/* 1ER LUGAR (ORO AZTECA - LÍDER DE LA COMPETENCIA) */}
                            <div className="col-12 col-md-4 order-1 order-md-2">
                                {firstPlace && (
                                    <div
                                        className={`glass-card-broadcast podium-first-card p-4 p-lg-5 rounded-4 text-center position-relative ${recentlyUpdatedIds.has(firstPlace.id) ? 'emerald-pulse-active' : ''}`}
                                    >
                                        <div
                                            className="position-absolute top-0 start-50 translate-middle badge rounded-pill px-4 py-2 shadow-lg"
                                            style={{
                                                backgroundColor: '#FFD700',
                                                color: '#0a0e14',
                                                fontWeight: 900,
                                                fontSize: '15px',
                                                border: '2px solid #FFF8E1',
                                                boxShadow: '0 0 20px rgba(255, 215, 0, 0.8)'
                                            }}
                                        >
                                            🥇 1º LUGAR • CAMPEÓN
                                        </div>

                                        <div className="mt-2 mb-1 trophy-badge-glow">
                                            <i className="bi bi-trophy-fill text-warning display-4" />
                                        </div>

                                        <h3 className="fw-black text-white mb-1 text-truncate" style={{ fontWeight: 900 }} title={firstPlace.displayName}>
                                            {firstPlace.displayName}
                                        </h3>

                                        <div className="d-inline-flex align-items-baseline gap-1 my-2">
                                            <span className="display-4 fw-black gold-text-shimmer" style={{ fontWeight: 900 }}>
                                                {firstPlace.decibelsPeak}
                                            </span>
                                            <span className="text-warning fw-bold fs-4">dB</span>
                                        </div>

                                        {(() => {
                                            const catStyle = getCategoryStyle(firstPlace.category, firstPlace.decibelsPeak);
                                            return (
                                                <div>
                                                    <span
                                                        className="badge rounded-pill px-4 py-2 fw-bold text-uppercase text-truncate"
                                                        style={{
                                                            background: catStyle.badgeBg,
                                                            color: catStyle.textColor,
                                                            border: catStyle.border,
                                                            boxShadow: `0 0 15px ${catStyle.glow}`,
                                                            fontSize: '13px',
                                                            maxWidth: '100%'
                                                        }}
                                                    >
                                                        <i className={`bi ${catStyle.icon} me-1`} />
                                                        {catStyle.title}
                                                    </span>
                                                </div>
                                            );
                                        })()}
                                    </div>
                                )}
                            </div>

                            {/* 3ER LUGAR (BRONCE) */}
                            <div className="col-12 col-md-4 order-3">
                                {thirdPlace ? (
                                    <div
                                        className={`glass-card-broadcast podium-third-card p-4 rounded-4 text-center position-relative ${recentlyUpdatedIds.has(thirdPlace.id) ? 'emerald-pulse-active' : ''}`}
                                    >
                                        <div
                                            className="position-absolute top-0 start-50 translate-middle badge rounded-pill px-3 py-1 shadow"
                                            style={{ backgroundColor: '#CD7F32', color: '#ffffff', fontWeight: 900, fontSize: '13px', border: '2px solid #E6A060' }}
                                        >
                                            🥉 3º LUGAR
                                        </div>

                                        <div className="mt-2 mb-1">
                                            <i className="bi bi-award-fill text-warning fs-2" style={{ color: '#CD7F32' }} />
                                        </div>

                                        <h4 className="fw-bold text-white mb-1 text-truncate" title={thirdPlace.displayName}>
                                            {thirdPlace.displayName}
                                        </h4>

                                        <div className="d-inline-flex align-items-baseline gap-1 my-2">
                                            <span className="display-6 fw-black text-white" style={{ fontWeight: 900 }}>
                                                {thirdPlace.decibelsPeak}
                                            </span>
                                            <span className="text-secondary fw-bold fs-5">dB</span>
                                        </div>

                                        {(() => {
                                            const catStyle = getCategoryStyle(thirdPlace.category, thirdPlace.decibelsPeak);
                                            return (
                                                <div>
                                                    <span
                                                        className="badge rounded-pill px-3 py-1 fw-bold small text-truncate"
                                                        style={{
                                                            background: catStyle.badgeBg,
                                                            color: catStyle.textColor,
                                                            border: catStyle.border,
                                                            boxShadow: `0 0 10px ${catStyle.glow}`,
                                                            maxWidth: '100%'
                                                        }}
                                                    >
                                                        <i className={`bi ${catStyle.icon} me-1`} />
                                                        {catStyle.title}
                                                    </span>
                                                </div>
                                            );
                                        })()}
                                    </div>
                                ) : (
                                    <div className="glass-card-broadcast p-4 rounded-4 text-center border-secondary border-opacity-25 opacity-50">
                                        <span className="badge bg-secondary mb-2">🥉 3º LUGAR</span>
                                        <p className="text-secondary small mb-0">Posición disponible</p>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* ------------------------------------------------------------- */}
                        {/* B. TABLA TOP 4 AL 20 (GRID RESPONSIVO DE 2 COLUMNAS) */}
                        {/* ------------------------------------------------------------- */}
                        {remainingList.length > 0 && (
                            <div className="mt-2">
                                <div className="d-flex align-items-center justify-content-between mb-3 px-2">
                                    <div className="d-flex align-items-center gap-2">
                                        <i className="bi bi-list-ol text-warning fs-5" />
                                        <h5 className="mb-0 fw-bold text-uppercase tracking-wide text-white" style={{ fontSize: '15px', letterSpacing: '1px' }}>
                                            TABLA DE CLASIFICACIÓN (POSICIONES 4 AL 20)
                                        </h5>
                                    </div>
                                    <span className="text-secondary small">
                                        Mostrando Top {Math.min(rankingList.length, 20)}
                                    </span>
                                </div>

                                <div className="row g-2 g-lg-3">
                                    {remainingList.map((item, idx) => {
                                        const actualPosition = idx + 4;
                                        const isRecentlyUpdated = recentlyUpdatedIds.has(item.id);
                                        const catStyle = getCategoryStyle(item.category, item.decibelsPeak);

                                        return (
                                            <div key={item.id || idx} className="col-12 col-md-6">
                                                <div
                                                    className={`glass-card-broadcast p-3 rounded-4 d-flex align-items-center justify-content-between gap-3 ${isRecentlyUpdated ? 'emerald-pulse-active' : ''}`}
                                                    style={{ borderLeft: `4px solid ${actualPosition <= 10 ? '#006847' : '#CE1126'}` }}
                                                >
                                                    {/* Posición y Nombre del Participante */}
                                                    <div className="d-flex align-items-center gap-3 min-w-0">
                                                        <div
                                                            className="rounded-circle d-flex align-items-center justify-content-center fw-black flex-shrink-0 shadow-sm"
                                                            style={{
                                                                width: '38px',
                                                                height: '38px',
                                                                backgroundColor: actualPosition <= 10 ? 'rgba(0, 104, 71, 0.4)' : 'rgba(255, 255, 255, 0.08)',
                                                                border: actualPosition <= 10 ? '1px solid #00E676' : '1px solid rgba(255, 255, 255, 0.2)',
                                                                color: '#ffffff',
                                                                fontSize: '14px',
                                                                fontWeight: 900
                                                            }}
                                                        >
                                                            #{actualPosition}
                                                        </div>

                                                        <div className="min-w-0">
                                                            <div className="fw-bold text-white text-truncate fs-6" title={item.displayName}>
                                                                {item.displayName}
                                                            </div>
                                                            <div className="mt-1">
                                                                <span
                                                                    className="badge rounded-pill px-2 py-0 fw-semibold text-truncate"
                                                                    style={{
                                                                        background: catStyle.badgeBg,
                                                                        color: catStyle.textColor,
                                                                        fontSize: '10px',
                                                                        border: catStyle.border,
                                                                        maxWidth: '100%'
                                                                    }}
                                                                >
                                                                    <i className={`bi ${catStyle.icon} me-1`} />
                                                                    {catStyle.title}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {/* Decibel Pico */}
                                                    <div className="text-end flex-shrink-0">
                                                        <div className="d-flex align-items-baseline justify-content-end gap-1">
                                                            <span className="fs-5 fw-black text-white" style={{ fontWeight: 900 }}>
                                                                {item.decibelsPeak}
                                                            </span>
                                                            <span className="text-secondary small fw-bold">dB</span>
                                                        </div>
                                                        <span className="badge bg-black bg-opacity-50 text-secondary border border-secondary border-opacity-25" style={{ fontSize: '9px' }}>
                                                            PICO MÁXIMO
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </main>

            {/* ========================================================================= */}
            {/* 3. FOOTER DE BROADCAST PATRIO */}
            {/* ========================================================================= */}
            <footer className="position-relative z-1 px-4 py-2 px-lg-5 border-top border-white border-opacity-10 d-flex flex-wrap align-items-center justify-content-between gap-2 bg-black bg-opacity-60 text-secondary small">
                <div className="d-flex align-items-center gap-2">
                    <span className="badge rounded-pill bg-success px-2 py-1 text-white" style={{ fontSize: '10px' }}>
                        <i className="bi bi-shield-check me-1" /> PROTOCOLO SEGURO (CERO PII)
                    </span>
                    <span className="d-none d-sm-inline" style={{ fontSize: '11px' }}>
                        Los decibeles pico (dB peak) determinan la posición oficial de victoria.
                    </span>
                </div>

                <div className="d-flex align-items-center gap-2 ms-auto" style={{ fontSize: '11px' }}>
                    <span className="text-warning fw-bold">INTERAKTIA-V2</span>
                    <span>•</span>
                    <span>BROADCASTING ENGINE</span>
                </div>
            </footer>
        </div>
    );
};

export default PersonalNoisemeterRanking;
